import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Download, Filter, MoreVertical, Trash2, PenSquare, X, Bell, Users } from "lucide-react";
import LeadDialog from "@/pages/LeadDialog";
import LeadDetail from "@/pages/LeadDetail";
import PingDialog from "@/components/PingDialog";
import { shortDate, relativeLabel } from "@/lib/date";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { STATUSES, PRIORITY_COLORS, STATUS_COLORS, SOURCE_COLORS } from "@/lib/constants";

export default function Leads() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.role === "superadmin";
  const canReassign = isAdmin || !!user?.can_reassign;
  const [leads, setLeads] = useState([]);
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [accountTypeFilter, setAccountTypeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  const [pingLead, setPingLead] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const memberMap = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m])), [members]);
  const allSelected = leads.length > 0 && selected.size === leads.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(leads.map((l) => l.id)));
  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const clearSelection = () => setSelected(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== "all") params.status_filter = statusFilter;
      if (priorityFilter !== "all") params.priority = priorityFilter;
      if (productFilter !== "all") params.product = productFilter;
      if (accountTypeFilter !== "all") params.account_type = accountTypeFilter;
      if (isAdmin && assignedFilter !== "all") params.assigned_to = assignedFilter;
      if (dateFrom) params.created_from = dateFrom;
      if (dateTo) params.created_to = dateTo;
      const { data } = await api.get("/leads", { params });
      setLeads(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [statusFilter, priorityFilter, assignedFilter, productFilter, accountTypeFilter, dateFrom, dateTo]);

  useEffect(() => {
    // Refresh the product list whenever leads change (add/edit/delete)
    api.get("/leads/products").then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
  }, [leads.length]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/users").then((r) => setMembers(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  // Deep-link support: /dashboard/leads?open=<lead_id> opens the Lead Detail dialog
  // (used by the Ping alert banner).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get("open");
    if (openId && leads.length && !detailLead) {
      const target = leads.find((l) => l.id === openId);
      if (target) {
        setDetailLead(target);
        params.delete("open");
        const q = params.toString();
        navigate(`/dashboard/leads${q ? `?${q}` : ""}`, { replace: true });
      }
    }
    // eslint-disable-next-line
  }, [location.search, leads]);

  const onDelete = async (lead) => {
    if (!window.confirm(`Delete "${lead.name}"?`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/export/csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads.csv";
      a.click();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Leads</p>
          <h1 className="text-base font-semibold font-semibold mt-2">All Leads</h1>
          <p className="text-slate-600 mt-2">{leads.length} record{leads.length !== 1 && "s"}</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={exportCsv} className="border-slate-200 bg-slate-100/70 flex-1 sm:flex-initial" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={() => setShowCreate(true)} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white font-semibold flex-1 sm:flex-initial" data-testid="new-lead-btn">
            <Plus className="w-4 h-4 mr-2" /> New Lead
          </Button>
        </div>
      </header>

      <div className="glass rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap gap-3 lg:items-center">
        <div className="lg:flex-1 lg:min-w-[200px] relative sm:col-span-2 lg:col-span-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email, company…" className="pl-9 bg-slate-100/70 border-slate-200 h-10 w-full" data-testid="leads-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full lg:w-36 bg-slate-100/70 border-slate-200" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-full lg:w-36 bg-slate-100/70 border-slate-200" data-testid="priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
        {isAdmin && (
          <Select value={assignedFilter} onValueChange={setAssignedFilter}>
            <SelectTrigger className="w-full lg:w-44 bg-slate-100/70 border-slate-200" data-testid="assigned-filter"><SelectValue placeholder="Assigned to" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All members</SelectItem>
              {members.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        <Select value={productFilter} onValueChange={setProductFilter}>
          <SelectTrigger className="w-full lg:w-40 bg-slate-100/70 border-slate-200" data-testid="product-filter"><SelectValue placeholder="Product" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {products.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={accountTypeFilter} onValueChange={setAccountTypeFilter}>
          <SelectTrigger className="w-full lg:w-36 bg-slate-100/70 border-slate-200" data-testid="account-type-filter"><SelectValue placeholder="Account type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All accounts</SelectItem>
            <SelectItem value="prepaid">Prepaid</SelectItem>
            <SelectItem value="postpaid">Postpaid</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Added from</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              max={dateTo || undefined}
              className="w-full lg:w-40 bg-slate-100/70 border-slate-200 h-10 text-sm"
              data-testid="date-from-filter"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Added to</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              min={dateFrom || undefined}
              className="w-full lg:w-40 bg-slate-100/70 border-slate-200 h-10 text-sm"
              data-testid="date-to-filter"
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              className="mt-4 w-8 h-8 rounded-md border border-slate-200 bg-white hover:bg-rose-50 flex items-center justify-center text-slate-500 hover:text-rose-600"
              aria-label="Clear date filter"
              data-testid="clear-date-filter"
              title="Clear date filter"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {/* Quick preset chips */}
        <div className="flex items-center gap-1 flex-wrap w-full lg:w-auto lg:ml-auto" data-testid="date-presets">
          {[
            { k: "today", label: "Today", days: 0 },
            { k: "7d", label: "7 days", days: 7 },
            { k: "30d", label: "30 days", days: 30 },
          ].map((p) => (
            <button
              key={p.k}
              type="button"
              onClick={() => {
                const to = new Date();
                const from = new Date();
                if (p.days > 0) from.setDate(from.getDate() - p.days);
                const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                setDateFrom(fmt(from));
                setDateTo(fmt(to));
              }}
              className="text-[11px] px-2 py-1 rounded-full border border-slate-200 bg-white hover:border-[#1D63ED]/40 hover:text-[#1D63ED] text-slate-600"
              data-testid={`date-preset-${p.k}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="glass rounded-lg p-3 flex items-center gap-2 flex-wrap" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-slate-800">{selected.size} selected</span>
          <div className="h-4 w-px bg-slate-200 hidden sm:block" />
          <Select onValueChange={async (v) => {
            try {
              const { data } = await api.post("/leads/bulk-update", { ids: Array.from(selected), status: v });
              toast.success(`${data.updated} lead(s) updated`);
              clearSelection(); load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}>
            <SelectTrigger className="w-32 sm:w-36 h-8 bg-white border-slate-200" data-testid="bulk-status-select"><SelectValue placeholder="Change status" /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={async (v) => {
            try {
              const { data } = await api.post("/leads/bulk-update", { ids: Array.from(selected), priority: v });
              toast.success(`${data.updated} lead(s) updated`);
              clearSelection(); load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}>
            <SelectTrigger className="w-28 sm:w-32 h-8 bg-white border-slate-200" data-testid="bulk-priority-select"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>{["Low","Medium","High","Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          {canReassign && (
            <Select onValueChange={async (v) => {
              try {
                const { data } = await api.post("/leads/bulk-update", { ids: Array.from(selected), assigned_to: v === "unassigned" ? "" : v });
                toast.success(`${data.updated} lead(s) reassigned`);
                clearSelection(); load();
              } catch (e) { toast.error(formatApiError(e)); }
            }}>
              <SelectTrigger className="w-36 sm:w-40 h-8 bg-white border-slate-200" data-testid="bulk-assign-select"><SelectValue placeholder="Reassign to" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="ghost" size="sm" className="h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="bulk-delete-btn" onClick={async () => {
            if (!window.confirm(`Delete ${selected.size} lead(s)?`)) return;
            try {
              const { data } = await api.post("/leads/bulk-delete", { ids: Array.from(selected) });
              toast.success(`${data.deleted} lead(s) deleted`);
              clearSelection(); load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
          </Button>
          <button className="ml-auto text-slate-500 hover:text-slate-700" onClick={clearSelection} data-testid="bulk-clear" aria-label="Clear selection">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="solid-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all" aria-label="Select all" />
                </th>
                <th className="text-left px-5 py-3">Lead</th>
                <th className="text-left px-5 py-3">Contact</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Priority</th>
                <th className="text-left px-5 py-3">Budget</th>
                <th className="text-left px-5 py-3">Source</th>
                {isAdmin && <th className="text-left px-5 py-3">Assigned</th>}
                <th className="text-left px-5 py-3">Follow-up</th>
                <th className="text-left px-5 py-3">Added</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={11} className="px-6 py-10 text-center text-slate-500">Loading…</td></tr>)}
              {!loading && leads.length === 0 && (<tr><td colSpan={11} className="px-6 py-10 text-center text-slate-500">No leads found</td></tr>)}
              {leads.map((l) => (
                <tr key={l.id} className={`border-t border-slate-200/60 hover:bg-slate-50 cursor-pointer ${selected.has(l.id) ? "bg-blue-50/40" : ""}`} onClick={() => setDetailLead(l)} data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleOne(l.id)} data-testid={`select-${l.id}`} aria-label={`Select ${l.name}`} />
                  </td>
                  <td className="px-5 py-3">
                    <p className="font-medium">{l.name}</p>
                    {l.company && <p className="text-xs text-slate-500">{l.company}</p>}
                  </td>
                  <td className="px-5 py-3 text-slate-700 text-xs">
                    {l.phone && <div>{l.phone}</div>}
                    {l.email && <div className="text-slate-500">{l.email}</div>}
                  </td>
                  <td className="px-5 py-3">
                    <span className={STATUS_COLORS[l.status]}>
                      {l.status}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={PRIORITY_COLORS[l.priority]}>
                      {l.priority}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-medium">{l.budget ? `₹${Number(l.budget).toLocaleString()}` : "—"}</td>
                  <td className="px-5 py-3">
                    <span className={SOURCE_COLORS[l.source] || SOURCE_COLORS.Other}>{l.source}</span>
                  </td>
                  {isAdmin && (
                    <td className="px-5 py-3 text-slate-700 text-xs">
                      {memberMap[l.assigned_to]?.name || <span className="text-slate-400 italic">Unassigned</span>}
                    </td>
                  )}
                  <td className="px-5 py-3 text-slate-700 text-xs">{l.follow_up_date || "—"}</td>
                  <td className="px-5 py-3 text-slate-700 text-xs" data-testid={`lead-added-${l.id}`}>
                    <div className="font-medium text-slate-700">{shortDate(l.created_at)}</div>
                    <div className="text-[10px] text-slate-400">{relativeLabel(l.created_at)}</div>
                  </td>
                  <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`lead-actions-${l.id}`}><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditing(l)}><PenSquare className="w-3.5 h-3.5 mr-2" /> Edit</DropdownMenuItem>
                        {isAdmin && l.assigned_to && l.assigned_to !== user.id && (
                          <DropdownMenuItem onClick={() => setPingLead(l)} className="text-rose-600 focus:text-rose-700" data-testid={`ping-action-${l.id}`}>
                            <Bell className="w-3.5 h-3.5 mr-2" /> Ping assignee
                          </DropdownMenuItem>
                        )}
                        {canReassign && (
                          <DropdownMenuItem onClick={() => setDetailLead(l)} data-testid={`reassign-action-${l.id}`}>
                            <Users className="w-3.5 h-3.5 mr-2" /> Reassign…
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => onDelete(l)} className="text-red-400"><Trash2 className="w-3.5 h-3.5 mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <LeadDialog
        open={showCreate || !!editing}
        onOpenChange={(v) => { if (!v) { setShowCreate(false); setEditing(null); } }}
        lead={editing}
        members={members}
        onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
      />

      <LeadDetail
        lead={detailLead}
        open={!!detailLead}
        onOpenChange={(v) => !v && setDetailLead(null)}
        onChanged={load}
        members={members}
      />

      <PingDialog
        lead={pingLead}
        open={!!pingLead}
        onOpenChange={(v) => !v && setPingLead(null)}
        memberName={pingLead ? memberMap[pingLead.assigned_to]?.name || "" : ""}
      />
    </div>
  );
}
